/**
 * Closed academic year lock — client side.
 *
 * The authority is the DB trigger `a_deny_closed_year_school_writes`, which
 * denies with SQLSTATE PT403. Everything here is UX: it stops users walking
 * into a wall, and turns the wall into a sentence when they do.
 *
 * Two rules this module exists to enforce:
 *  1. FAIL CLOSED while state is unresolved. The existing AcademicYearContext
 *     fails OPEN on a settings fetch error, which is how a network blip could
 *     unlock a year.
 *  2. Gate on an EXPLICIT year, never on "the current year". Several actions
 *     target a year other than the selected one (move-up writes to next year;
 *     the transportation dialog keys on the row's own academic_year).
 */

/** SQLSTATE raised by the DB trigger. PostgREST maps PT<xyz> to HTTP <xyz>. */
export const YEAR_LOCK_SQLSTATE = 'PT403';

/** Prefix on the trigger's message, for callers that only get a string. */
export const YEAR_LOCK_MESSAGE_PREFIX = 'ACADEMIC_YEAR_LOCKED';

export type YearGateState =
  | 'unresolved' // still loading — treat as locked
  | 'editable'
  | 'locked'
  | 'error'; // lookup failed — treat as locked, permanently, until reload

export interface YearGateInput {
  /** Years closed to school users. `null` means NOT YET KNOWN. */
  closedYears: string[] | null;
  /** True once year, roles and the closed-year list have all resolved. */
  ready: boolean;
  /** A lookup failed; stay locked rather than guessing. */
  errored?: boolean;
  /** True for NWSISD admin / super_admin, who may override. */
  isPrivilegedEditor?: boolean;
}

/**
 * The whole decision, as a pure function. Mirrors the DB's
 * `year_lock_decision`, minus the service-role branch (browsers are never
 * service_role).
 */
export function resolveYearGate(
  year: string | null | undefined,
  input: YearGateInput,
): YearGateState {
  const { closedYears, ready, errored } = input;

  // Fail closed: anything unknown is treated as locked.
  if (errored) return 'error';
  if (!ready) return 'unresolved';
  if (closedYears === null || !Array.isArray(closedYears)) return 'unresolved';

  // A year we cannot identify cannot be proven open.
  if (!year || typeof year !== 'string' || !year.trim()) return 'unresolved';

  return closedYears.includes(year.trim()) ? 'locked' : 'editable';
}

/** Can the current user write this specific year? Privileged editors always can. */
export function canWriteYear(
  year: string | null | undefined,
  input: YearGateInput,
): boolean {
  const state = resolveYearGate(year, input);
  if (state === 'editable') return true;
  // Admins may override a *locked* year, but not an unresolved/errored one —
  // if we do not know the state, nobody writes.
  if (state === 'locked' && input.isPrivilegedEditor) return true;
  return false;
}

/** True when an admin is about to edit a closed year and should confirm first. */
export function needsClosedYearConfirm(
  year: string | null | undefined,
  input: YearGateInput,
): boolean {
  return (
    resolveYearGate(year, input) === 'locked' && !!input.isPrivilegedEditor
  );
}

/** Did this Supabase/PostgREST error come from the year lock? */
export function isYearLockedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: unknown; message?: unknown; details?: unknown };
  if (e.code === YEAR_LOCK_SQLSTATE) return true;
  // Some RPC wrappers stringify the error and drop the code.
  const text = [e.message, e.details].filter((v) => typeof v === 'string').join(' ');
  return text.includes(YEAR_LOCK_MESSAGE_PREFIX);
}

/** Pull the year out of the trigger's message, when present. */
export function yearFromLockError(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const msg = (error as { message?: unknown }).message;
  if (typeof msg !== 'string') return null;
  const m = msg.match(/(\d{4}-\d{4})/);
  return m ? m[1] : null;
}

/** What a school user sees. Never surface the raw Postgres error. */
export function yearLockedToast(year?: string | null): string {
  const y = year || 'That school year';
  return `${y} is closed for changes. Nothing was saved. Switch to the current year to make changes.`;
}

/** Tooltip / disabled-reason text on a gated control. */
export function yearLockedReason(year?: string | null): string {
  const y = year || 'This school year';
  return `${y} is closed. Switch years at the top of the page to make changes.`;
}

/**
 * A year that has not been rolled up yet. The DB trigger `a2_deny_adds_into_unopened_year`
 * refuses school adds into it with SQLSTATE PT409 (HTTP 409).
 */
export const YEAR_NOT_OPEN_SQLSTATE = 'PT409';
export const YEAR_NOT_OPEN_MESSAGE_PREFIX = 'YEAR_NOT_OPEN';

/** Did this Supabase/PostgREST error come from the not-yet-rolled-up year guard? */
export function isYearNotOpenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: unknown; message?: unknown };
  if (e.code === YEAR_NOT_OPEN_SQLSTATE) return true;
  return typeof e.message === 'string' && e.message.includes(YEAR_NOT_OPEN_MESSAGE_PREFIX);
}

/** What a school user sees when an add into a not-yet-rolled-up year is refused. */
export function yearNotOpenToast(year?: string | null): string {
  const y = year || 'That school year';
  return `${y} has not been rolled up yet, so students cannot be added to it. Nothing was saved. Add the student to the current school year; the roll-up carries them forward.`;
}
